import React, { useState, useEffect, useCallback } from "react";
import ListView from "../../../../components/common/ListView";
import { IconSearch, IconPlus, IconTrash, IconPencil, IconRefresh, IconX } from "@tabler/icons-react";
import { fetchHrLeaveTypes, deleteHrLeaveType } from "../../../../apis/hrApi";
import AddEditLeaveTypeModal from "./AddEditLeaveTypeModal";
import toast from "react-hot-toast";

export default function LeavesTab() {
  const [leavesData, setLeavesData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Sidebar and Selected Item State
  const [sidebarMode, setSidebarMode] = useState<"ADD" | "EDIT" | "DELETE" | null>(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState<any | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadLeaveTypes = useCallback(async (searchVal = "") => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchHrLeaveTypes(1, 50, searchVal);
      if (res.success) {
        const list = res.data?.leaveTypes || res.data || [];
        setLeavesData(list);
      } else {
        setError("Failed to load leave types");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load leave types");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadLeaveTypes(searchQuery);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, loadLeaveTypes]);

  const handleConfirmDelete = async () => {
    if (!selectedLeaveType) return;
    setIsDeleting(true);
    try {
      const id = selectedLeaveType.id || selectedLeaveType._id;
      const res = await deleteHrLeaveType(id);
      if (res.success) {
        toast.success("Leave type deleted successfully");
        loadLeaveTypes();
        setSidebarMode(null);
        setSelectedLeaveType(null);
      } else {
        toast.error(res.message || "Failed to delete leave type");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete leave type");
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      label: "LEAVE TYPE",
      render: (s: any) => (
        <span
          className="px-3 py-1 rounded-full text-xs font-semibold text-white shadow-sm whitespace-nowrap"
          style={{ backgroundColor: s.color || "#E53E3E" }}
        >
          {s.leaveType}
        </span>
      ),
    },
    { label: "NO OF LEAVE", render: (s: any) => <span className="text-sm font-medium">{s.noOfLeaves ?? s.noOfLeave ?? 1}</span> },
    { label: "MONTHLY LIMIT", render: (s: any) => <span className="text-sm">{s.monthlyLimit}</span> },
    { label: "LEAVE PAID STATUS", render: (s: any) => <span className="text-sm">{s.isPaidLeave ? "Yes" : "No"}</span> },
    {
      label: "DEPARTMENTS",
      render: (s: any) => {
        const text = Array.isArray(s.departments)
          ? s.departments.map((d: any) => typeof d === "object" ? d.departmentName || d.name : d).join(", ")
          : s.departments || "-";
        return <span className="text-sm text-blue-600 font-medium whitespace-nowrap max-w-[120px] overflow-hidden text-ellipsis block">{text || "-"}</span>;
      }
    },
    {
      label: "DESIGNATION",
      render: (s: any) => {
        const text = Array.isArray(s.designations)
          ? s.designations.map((d: any) => typeof d === "object" ? d.designationName || d.name : d).join(", ")
          : s.designation || "-";
        return <span className="text-sm text-blue-600 font-medium whitespace-nowrap max-w-[120px] overflow-hidden text-ellipsis block">{text || "-"}</span>;
      }
    },
    {
      label: "ACTIONS",
      render: (s: any) => (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => {
              setSelectedLeaveType(s);
              setSidebarMode("EDIT");
            }}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition text-slate-500 cursor-pointer bg-white"
            title="Edit"
          >
            <IconPencil size={15} stroke={1.5} />
          </button>
          <button
            onClick={() => {
              setSelectedLeaveType(s);
              setSidebarMode("DELETE");
            }}
            className="w-8 h-8 rounded-full border border-red-100 flex items-center justify-center hover:bg-red-50 transition text-red-500 cursor-pointer bg-white"
            title="Delete"
          >
            <IconTrash size={15} stroke={1.5} />
          </button>
        </div>
      ),
    },
  ];

  const displayedData = leavesData;

  const isSidebarOpen = sidebarMode !== null;

  // Filter columns when sidebar is open to make the list view fit nicely
  const activeColumns = isSidebarOpen
    ? columns.filter((_, idx) => [0, 1, 4, 6].includes(idx)) // Show LEAVE TYPE, NO OF LEAVE, DEPARTMENTS, ACTIONS
    : columns;

  const sidebarContent = sidebarMode ? (
    <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 shrink-0 border-b border-gray-100 bg-white">
        <h2 className="text-lg font-bold text-[#212121]">
          {sidebarMode === "ADD"
            ? "New Leave Type"
            : sidebarMode === "EDIT"
            ? "Edit Leave Type"
            : "Delete Leave"}
        </h2>
        <button
          type="button"
          onClick={() => {
            setSidebarMode(null);
            setSelectedLeaveType(null);
          }}
          className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
          title="Close"
        >
          <IconX size={18} />
        </button>
      </div>

      {sidebarMode === "DELETE" ? (
        <div className="p-6 bg-white space-y-6">
          <p className="text-sm text-gray-600 font-medium">
            Are you sure you want to delete this Leave type?
          </p>
          {selectedLeaveType && (
            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">LEAVE TYPE</span>
              <span
                className="px-3 py-1 rounded-full text-xs font-semibold text-white shadow-sm"
                style={{ backgroundColor: selectedLeaveType.color || "#E53E3E" }}
              >
                {selectedLeaveType.leaveType}
              </span>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                setSidebarMode(null);
                setSelectedLeaveType(null);
              }}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] text-xs font-bold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm hover:shadow-md duration-150 hover:-translate-y-0.5 active:translate-y-0"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      ) : (
        <AddEditLeaveTypeModal
          isOpen={sidebarMode === "ADD" || sidebarMode === "EDIT"}
          onClose={() => {
            setSidebarMode(null);
            setSelectedLeaveType(null);
          }}
          onSave={loadLeaveTypes}
          leaveTypeToEdit={selectedLeaveType}
          isSidebar={true}
        />
      )}
    </div>
  ) : null;

  return (
    <div className="w-full">
      <div className="grid grid-cols-12 gap-6 p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 rounded-r-3xl rounded-bl-3xl w-full mt-2 animate-in fade-in duration-300 items-start">
        <div className={isSidebarOpen ? "col-span-12 xl:col-span-7" : "col-span-12"}>
          <div className="bg-white rounded-3xl border border-gray-200/50 shadow-sm overflow-hidden flex flex-col justify-start">
            <div className="px-6 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100">
              <h2 className="text-lg font-medium text-[#212121]">Leaves Info</h2>
              <div className="flex items-center flex-wrap gap-2.5 justify-start sm:justify-end">
                {/* Search Sliding Input Box */}
                <div className="relative flex items-center group">
                  <div
                    onClick={() => {
                      if (!showSearch) {
                        setShowSearch(true);
                      }
                    }}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch
                        ? "w-48 px-2 cursor-default"
                        : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                      }}
                      className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                    >
                      <IconSearch size={17} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          placeholder="Search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none animate-in fade-in duration-200"
                        />
                        {searchQuery && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchQuery("");
                            }}
                            className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                          >
                            <IconX size={14} stroke={2.5} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={() => loadLeaveTypes(searchQuery)}
                  className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                  title="Refresh"
                >
                  <IconRefresh size={17} stroke={2.5} />
                </button>

                {/* Add Leave Type Button */}
                <button
                  onClick={() => {
                    setSelectedLeaveType(null);
                    setSidebarMode("ADD");
                  }}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#e2bd30] border border-[#FBD037] text-[#212121] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer"
                  title="Add Leave"
                >
                  <IconPlus stroke={2.5} size={17} />
                </button>
              </div>
            </div>

            <div className="pb-2">
              {isLoading ? (
                <div className="p-8 flex justify-center items-center">
                  <svg
                    className="animate-spin h-6 w-6 text-[#FBD037]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                </div>
              ) : error ? (
                <div className="p-8 text-center text-red-500 font-semibold">{error}</div>
              ) : (
                <>
                  <ListView
                    mainDivClassName="bg-transparent"
                    tdClassName="rounded-r-none rounded-l-none"
                    trClassName="hover:bg-[#FBD037]/20 transition-colors border-b border-gray-100"
                    mailDivClassName="p-0 bg-transparent"
                    thClassName="border-t"
                    columns={activeColumns}
                    data={displayedData}
                  />
                  <div className="flex justify-end px-6 py-4 text-sm text-gray-500 items-center gap-2">
                    <span>Record 1-{displayedData.length} of {displayedData.length}</span>
                    <span className="mx-2">10</span>
                    <button className="opacity-50" disabled>&lt;</button>
                    <span>1/1</span>
                    <button className="opacity-50" disabled>&gt;</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Sidebar (static column inside grid) */}
        {isSidebarOpen && (
          <div className="hidden xl:block xl:col-span-5 space-y-4 mt-2">
            {sidebarContent}
          </div>
        )}
      </div>

      {/* Mobile/Tablet Sidebar (fixed overlay outside grid) */}
      {isSidebarOpen && (
        <div className="xl:hidden">
          <div
            className="fixed inset-0 bg-black/40 z-30"
            onClick={() => {
              setSidebarMode(null);
              setSelectedLeaveType(null);
            }}
          />
          <div className="fixed inset-y-0 right-0 z-[35] w-full sm:w-[500px] p-4 pt-[76px] sm:pt-[96px] shadow-2xl bg-[#F7F6F2] overflow-y-auto flex flex-col justify-start">
            {sidebarContent}
          </div>
        </div>
      )}
    </div>
  );
}
